import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import {
    getShareComments, replyShareComment, resolveShareComment, markShareCommentsSeen, removeShareComment,
} from '../services/api';

// The student's notes from reviewers (#4987, ADR 0062), for every surface that
// shows them (the Launchpad's badges, the Sharing page's threads). One read
// per page visit, shared by every consumer on the page through a module
// store, refreshed after the student's own reply or resolve.
//
// `inbox` is null while loading or off, false when the read failed (a failed
// read is never shown as "no notes", app-page rule 5), else the server's
// {links, threads_by_item, open_by_item, unread_by_item, open_total,
// unread_total}. Counts refresh after the student's own reply, resolve,
// remove or opening a thread, and on the next page load.

let store = { email: null, inbox: null, promise: null };
const listeners = new Set();

function publish(next) {
    store = { ...store, ...next };
    listeners.forEach((fn) => fn(store.inbox));
}

let latest = 0; // the newest read wins: an older reload never overwrites it

function load(email, force = false) {
    if (!force && store.email === email && (store.inbox || store.promise)) return store.promise;
    const mine = ++latest;
    const current = () => store.email === email && mine === latest;
    // Promise.resolve().then: even a synchronous throw becomes the failed
    // state, so a notes failure never takes down the page around a badge.
    const promise = Promise.resolve().then(() => getShareComments(email))
        .then((inbox) => { if (current()) publish({ inbox, promise: null }); })
        .catch(() => { if (current()) publish({ inbox: false, promise: null }); });
    store = { email, inbox: store.email === email ? store.inbox : null, promise };
    return promise;
}

export function resetShareNotesStore() {
    store = { email: null, inbox: null, promise: null };
}

export default function useShareNotes() {
    const on = useFeatureFlag('share_comments');
    const { currentUser } = useAuth();
    const email = currentUser?.email || null;
    const [inbox, setInbox] = useState(() => (store.email === email ? store.inbox : null));

    useEffect(() => {
        if (!on || !email) return undefined;
        listeners.add(setInbox);
        load(email);
        setInbox(store.email === email ? store.inbox : null);
        return () => { listeners.delete(setInbox); };
    }, [on, email]);

    const reply = useCallback(async (grantId, commentId, body) => {
        const res = await replyShareComment(email, grantId, commentId, body);
        await load(email, true);
        return res;
    }, [email]);

    const resolve = useCallback(async (grantId, commentId, resolved) => {
        const res = await resolveShareComment(email, grantId, commentId, resolved);
        await load(email, true);
        return res;
    }, [email]);

    const remove = useCallback(async (grantId, commentId) => {
        const res = await removeShareComment(email, grantId, commentId);
        await load(email, true);
        return res;
    }, [email]);

    // Opening a thread marks the reviewer's words in it seen. Best effort:
    // a failed mark leaves them unread, which is the honest side.
    const markSeen = useCallback(async (byGrant) => {
        const jobs = Object.entries(byGrant || {}).filter(([, ids]) => ids.length)
            .map(([grantId, ids]) => markShareCommentsSeen(email, grantId, ids));
        if (!jobs.length) return;
        await Promise.allSettled(jobs);
        await load(email, true);
    }, [email]);

    return { on: on && !!email, inbox: on ? inbox : null, reply, resolve, remove, markSeen };
}
